import type {ZodType} from "zod";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {GeographyMasterCache} from "~/domainModels/indexedDb/geographyDatabase";
import {buildPlutoCacheableGetUrl} from "~/utilities/plutoCacheableGetUrl";

function geographyMasterCacheKeyPrefix(apiOrigin: string, path: string): string {
    return `vesta:geography:v1:${new URL(apiOrigin).origin}:${path}:`;
}

export function geographyMasterCacheKey(apiOrigin: string, path: string, revisionId: Uuid): string {
    return `${geographyMasterCacheKeyPrefix(apiOrigin, path)}${revisionId}`;
}

const STORED_SAMPLE_SIZE = 100;

/**
 * Stored datasets were fully validated before being written, so a read only needs to detect a shape
 * change (e.g. a new required field without a revision bump). Every row shares one shape, so an even
 * sample including both ends catches that at ~0.1ms instead of ~60ms for 75k pincodes.
 */
function isStoredDatasetValid<T>(stored: unknown, validator: ZodType<T>): stored is Array<T> {
    if (!Array.isArray(stored)) {
        return false;
    }
    if (stored.length === 0) {
        return true;
    }
    const step = Math.max(1, Math.floor((stored.length - 1) / (STORED_SAMPLE_SIZE - 1)));
    for (let index = 0; index < stored.length; index += step) {
        if (!validator.safeParse(stored[index]).success) {
            return false;
        }
    }
    return validator.safeParse(stored[stored.length - 1]).success;
}

export async function loadGeographyMaster<T>({
    intellsysBaseUrl,
    path,
    revisionId,
    validator,
    cache,
}: {
    intellsysBaseUrl: string;
    path: string;
    revisionId: Uuid;
    validator: ZodType<T>;
    cache: GeographyMasterCache;
}): Promise<Array<T>> {
    const key = geographyMasterCacheKey(intellsysBaseUrl, path, revisionId);
    // Unavailable/evicted/corrupt browser storage is a cache miss, never a reason to block the app.
    try {
        const stored = await cache.get(key);
        if (stored !== undefined) {
            if (isStoredDatasetValid(stored, validator)) {
                return stored;
            }
            await cache.remove(key);
        }
    } catch {
        // Fetch validated data below when IndexedDB is unavailable.
    }

    const url = new URL(buildPlutoCacheableGetUrl({input: {}, intellsysBaseUrl, path}));
    url.searchParams.set("revision", revisionId);
    // On a local miss obtain a body, never an old browser-cache response or a bare 304.
    const response = await fetch(url.toString(), {method: "GET", cache: "no-store"});
    if (!response.ok) {
        throw new Error(`Unable to load geography master ${path}: HTTP ${response.status}`);
    }
    const body: unknown = await response.json();
    if (typeof body !== "object" || body === null || !("success" in body) || body.success !== true || !("data" in body)) {
        throw new Error(`Unable to load geography master ${path}`);
    }
    const data = validator.array().parse(body.data);
    const servedRevision = response.headers.get("ETag")?.match(/^(?:W\/)?"[^":]+:([^"\s]+)"$/u)?.[1];
    // During a rolling deployment an older server may answer. Use its validated response,
    // but never persist it under a different revision's key.
    if (servedRevision === revisionId) {
        try {
            await cache.put(key, data);
        } catch {
            // A full/disabled store must not discard a successful API response.
        }
        try {
            await cache.removeByPrefixExcept(geographyMasterCacheKeyPrefix(intellsysBaseUrl, path), key);
        } catch {
            // Superseded revisions are retried on the next miss; they are never read.
        }
    }
    return data;
}
